import React from "react";
import {Button, Card, Col, Container, Form, Row} from "react-bootstrap";
import {useForm, usePage} from "@inertiajs/react";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import SelectList from "../../Components/SelectList.jsx";
import NutzungSpeicherUebersicht from "../../Components/Nutzung/NutzungSpeicherUebersicht.jsx";
import NutzungMitarbeiterAuswahl from "../../Components/Nutzung/NutzungMitarbeiterAuswahl.jsx";

const IP_OPTIONEN = [
    {value: "full", label: "Vollständige IP-Adresse"},
    {value: "short", label: "Gekürzte IP-Adresse (letzte Gruppe entfernt)"},
    {value: "none", label: "Keine IP-Adresse"},
];

/**
 * Einstellungen der Nutzungsstatistik: die fünf Erfassungs-Schalter
 * (Spezifikation Abschnitt 3, gespeichert über POST /nutzung/einstellungen)
 * sowie darunter der Speicher-/Aufräumbereich (Abschnitt 10, eigener
 * JSON-Endpunkt /nutzung/daten/speicher).
 */
export default function NutzungEinstellungen() {
    const {einstellungen, alleMitarbeiter} = usePage().props;
    // Bewusst die VOLLSTÄNDIGE Mitarbeiterliste (siehe Controller-Kommentar
    // zu 'alleMitarbeiter') - anders als die übrigen Filterlisten der
    // Auswertung, die bereits ausgeblendete Mitarbeiter nicht mehr zeigen.
    // Hier muss sich auch ein bereits ausgeblendeter Mitarbeiter wieder
    // AUSWÄHLEN (= einblenden) lassen können.
    const mitarbeiterOptionen = (alleMitarbeiter || []).map((m) => ({value: String(m.id), label: m.name}));
    // Die Kopfleiste von NutzungLayout gehört zum gemeinsamen Rahmen (Teil 8)
    // - ihr Zeitraum-Filter hat für ein Einstellungsformular keine Wirkung,
    // deshalb hier ohne Mitarbeiter-/Bereichsauswahl (showMitarbeiter/
    // showBereich = false), Zeitraum-Schnellwahl bleibt Teil des Rahmens.
    const nutzungFilters = useNutzungFilter();
    const {data, setData, post, processing, errors, recentlySuccessful} = useForm({
        ACTIVITY_TRACKING: !!einstellungen.ACTIVITY_TRACKING,
        ACTIVITY_TRACKING_CLIENT: !!einstellungen.ACTIVITY_TRACKING_CLIENT,
        ACTIVITY_TRACKING_CHANGES: !!einstellungen.ACTIVITY_TRACKING_CHANGES,
        ACTIVITY_TRACK_IP: einstellungen.ACTIVITY_TRACK_IP || "short",
        ACTIVITY_RETENTION_DAYS: einstellungen.ACTIVITY_RETENTION_DAYS ?? 0,
        ACTIVITY_EXCLUDED_EMAILS: einstellungen.ACTIVITY_EXCLUDED_EMAILS || "",
        ACTIVITY_HIDDEN_USERS: einstellungen.ACTIVITY_HIDDEN_USERS || [],
    });

    const speichern = (e) => {
        e.preventDefault();
        post("/nutzung/einstellungen");
    };

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout gruppe="Verwaltung" titel="Erfassung + Speicher" nutzungFilters={nutzungFilters} showMitarbeiter={false} showBereich={false}>
            <Form onSubmit={speichern} className="d-flex flex-column gap-3">
                <Card className="shadow-sm">
                    <Card.Header className="fw-semibold">Erfassung</Card.Header>
                    <Card.Body className="d-flex flex-column gap-4">
                        <Schalter
                            id="ACTIVITY_TRACKING"
                            label="Nutzung erfassen"
                            checked={data.ACTIVITY_TRACKING}
                            onChange={(v) => setData("ACTIVITY_TRACKING", v)}
                            error={errors.ACTIVITY_TRACKING}
                        >
                            Hauptschalter für die gesamte Nutzungsstatistik. Ist er aus, wird nichts
                            protokolliert - auch wenn die Schalter darunter aktiv sind (fail closed: ein
                            unklarer Zustand bedeutet immer "keine Erfassung").
                        </Schalter>

                        <Schalter
                            id="ACTIVITY_TRACKING_CLIENT"
                            label="Browser-Ereignisse mit erfassen"
                            checked={data.ACTIVITY_TRACKING_CLIENT}
                            onChange={(v) => setData("ACTIVITY_TRACKING_CLIENT", v)}
                            error={errors.ACTIVITY_TRACKING_CLIENT}
                            disabled={!data.ACTIVITY_TRACKING}
                        >
                            Erfasst zusätzlich Klicks, Filter, Dialoge und die Verweildauer je Seite aus
                            dem Browser. Ohne diesen Schalter zählen nur die Aufrufe, die der Server
                            ohnehin sieht (Seitenaufrufe, Datenabrufe, Aktionen).
                        </Schalter>

                        <Schalter
                            id="ACTIVITY_TRACKING_CHANGES"
                            label="Datenänderungen mit erfassen"
                            checked={data.ACTIVITY_TRACKING_CHANGES}
                            onChange={(v) => setData("ACTIVITY_TRACKING_CHANGES", v)}
                            error={errors.ACTIVITY_TRACKING_CHANGES}
                            disabled={!data.ACTIVITY_TRACKING}
                        >
                            Protokolliert Änderungen an beobachteten Datensätzen (u. a. Bestellungen,
                            Benutzer, Rollen, Kampagnen) mit Vorher- und Nachher-Wert je Feld. Passwörter,
                            Token und andere Geheimnisse werden dabei nie mitgeschrieben.
                        </Schalter>

                        <Form.Group as={Row} controlId="ACTIVITY_TRACK_IP">
                            <Form.Label column sm={4} className="fw-semibold">
                                IP-Adresse
                            </Form.Label>
                            <Col sm={8}>
                                <SelectList
                                    id="ACTIVITY_TRACK_IP"
                                    value={data.ACTIVITY_TRACK_IP}
                                    options={IP_OPTIONEN}
                                    onChange={(v) => setData("ACTIVITY_TRACK_IP", v)}
                                    ariaLabel="IP-Adresse"
                                    disabled={!data.ACTIVITY_TRACKING}
                                    style={{maxWidth: 340}}
                                />
                                <div className="small text-muted mt-1">
                                    Wie viel von der IP-Adresse eines Nutzers mitgespeichert wird.
                                </div>
                                {errors.ACTIVITY_TRACK_IP && (
                                    <div className="invalid-feedback d-block">{errors.ACTIVITY_TRACK_IP}</div>
                                )}
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} controlId="ACTIVITY_RETENTION_DAYS">
                            <Form.Label column sm={4} className="fw-semibold">
                                Automatisches Aufräumen
                            </Form.Label>
                            <Col sm={8}>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        max={3650}
                                        value={data.ACTIVITY_RETENTION_DAYS}
                                        onChange={(e) => setData("ACTIVITY_RETENTION_DAYS", e.target.value)}
                                        style={{maxWidth: 120}}
                                        className={errors.ACTIVITY_RETENTION_DAYS ? "is-invalid" : ""}
                                    />
                                    <span className="text-muted">Tage (0 = unbegrenzt)</span>
                                </div>
                                <div className="small text-muted mt-1">
                                    Läuft nachts automatisch. Standard ist 0 - unbegrenzte Aufbewahrung,
                                    nichts wird von selbst gelöscht. Unabhängig davon lässt sich weiter
                                    unten jederzeit manuell und gezielt aufräumen.
                                </div>
                                {errors.ACTIVITY_RETENTION_DAYS && (
                                    <div className="invalid-feedback d-block">{errors.ACTIVITY_RETENTION_DAYS}</div>
                                )}
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} controlId="ACTIVITY_EXCLUDED_EMAILS">
                            <Form.Label column sm={4} className="fw-semibold">
                                Weitere ausgenommene Adressen
                            </Form.Label>
                            <Col sm={8}>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={data.ACTIVITY_EXCLUDED_EMAILS}
                                    onChange={(e) => setData("ACTIVITY_EXCLUDED_EMAILS", e.target.value)}
                                    placeholder="sales@molotow.com, weitere@molotow.com"
                                    disabled={!data.ACTIVITY_TRACKING}
                                    className={errors.ACTIVITY_EXCLUDED_EMAILS ? "is-invalid" : ""}
                                />
                                <div className="small text-muted mt-1">
                                    Kommagetrennte E-Mail-Adressen, die von der Nutzungsstatistik ausgenommen
                                    werden - z. B. Sammelzugänge, über die mehrere Personen arbeiten. Der
                                    Superadmin ist unabhängig davon immer ausgenommen.
                                </div>
                                {errors.ACTIVITY_EXCLUDED_EMAILS && (
                                    <div className="invalid-feedback d-block">{errors.ACTIVITY_EXCLUDED_EMAILS}</div>
                                )}
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="shadow-sm">
                    <Card.Header className="fw-semibold">Darstellung</Card.Header>
                    <Card.Body className="d-flex flex-column gap-2">
                        <Form.Group controlId="ACTIVITY_HIDDEN_USERS">
                            <Form.Label className="fw-semibold">In der Darstellung ausgeblendete Mitarbeiter</Form.Label>
                            <div className="small text-muted mb-2" style={{maxWidth: 640}}>
                                Diese Mitarbeiter erscheinen in KEINER Auswertung mehr - weder in
                                Kennzahlen, Ranglisten, Wegverlauf noch in den Mitarbeiter-Auswahllisten
                                der Filter. Ihre Daten werden dabei weiter ganz normal erfasst und bleiben
                                vollständig in der Datenbank (z. B. für ein Auskunftsersuchen); nur die
                                Darstellung blendet sie aus. Das ist etwas anderes als die "weiteren
                                ausgenommenen Adressen" oben: jene verhindern die Erfassung selbst, hier
                                geht es nur um das Anzeigen bereits vorhandener Daten. Eine Ausnahme gibt
                                es: das Zugriffsprotokoll (Nutzung → Zugriffe) zeigt weiterhin JEDEN
                                Zugriff, auch von ausgeblendeten Mitarbeitern - es ist der Nachweis, wer
                                wann in die Nutzungsdaten geschaut hat, und muss deshalb vollständig
                                bleiben.
                            </div>
                            <NutzungMitarbeiterAuswahl
                                options={mitarbeiterOptionen}
                                selected={data.ACTIVITY_HIDDEN_USERS}
                                onChange={(next) => setData("ACTIVITY_HIDDEN_USERS", next)}
                            />
                            {errors.ACTIVITY_HIDDEN_USERS && (
                                <div className="invalid-feedback d-block">{errors.ACTIVITY_HIDDEN_USERS}</div>
                            )}
                        </Form.Group>
                    </Card.Body>
                    <Card.Footer className="d-flex align-items-center gap-3">
                        <Button type="submit" disabled={processing}>
                            Einstellungen speichern
                        </Button>
                        {recentlySuccessful && <span className="text-success small">Gespeichert.</span>}
                    </Card.Footer>
                </Card>
            </Form>

            <div className="mt-4">
                <NutzungSpeicherUebersicht />
            </div>
            </NutzungLayout>
        </Container>
    );
}

function Schalter({id, label, checked, onChange, error, disabled, children}) {
    return (
        <Form.Group>
            <Form.Check
                type="switch"
                id={id}
                label={<span className="fw-semibold">{label}</span>}
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
            />
            <div className="small text-muted mt-1" style={{maxWidth: 640}}>
                {children}
            </div>
            {error && <div className="invalid-feedback d-block">{error}</div>}
        </Form.Group>
    );
}
